import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../booking/api";
import { BookShell } from "../booking/BookShell";
import { ServicePicker } from "../booking/ServicePicker";
import { DateTimePicker } from "../booking/DateTimePicker";
import { GuestForm } from "../booking/GuestForm";
import { Confirmation } from "../booking/Confirmation";
import { prettyDate, priceLabel } from "../booking/utils";
import { applySeoHead } from "@/lib/seoHead";
import { useBT } from "../booking/i18n";
import { PriorityGate } from "../booking/PriorityGate";
import { ArrowLeft } from "lucide-react";

export default function BookPage() {
  const { t, lang, locale } = useBT();
  useEffect(() => {
    applySeoHead("book");
    document.documentElement.lang = lang;
  }, [lang]);
  const [params] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(params.get("date"));
  const [time, setTime] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [fullDay, setFullDay] = useState(false);
  const [priority, setPriority] = useState(null);
  const [verified, setVerified] = useState(false);
  const priorityCode = params.get("priority") || sessionStorage.getItem("tribe_priority");

  useEffect(() => {
    if (!priorityCode) return;
    api.get(`/api/public/priority/${priorityCode}`).then((r) => {
      sessionStorage.setItem("tribe_priority", priorityCode);
      setPriority(r.data);
    }).catch(() => sessionStorage.removeItem("tribe_priority"));
  }, [priorityCode]);

  useEffect(() => {
    api.get("/api/public/booking-catalog").then((r) => {
      setCatalog(r.data);
      const deep = r.data.services.find((s) => s.slug === params.get("service"));
      const visible = r.data.categories.filter((c) => c.visible);
      const cat = visible.find((c) => c.slug === params.get("category"));
      setActiveCat(deep?.category_id || cat?.id || visible[0]?.id);
      if (deep) { setService(deep); setStep(1); }
    }).catch(() => setCatalog({ categories: [], services: [] }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const gated = !!catalog?.priority?.active && !priority && !verified;
  const deposit = catalog?.payments_enabled && service?.deposit_enabled ? service.deposit_amount : 0;
  const pick = (s) => { setService(s); setTime(null); setStep(1); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async (guest) => {
    setSubmitting(true);
    try {
      const r = await api.post("/api/public/bookings", { service_id: service.id, date, time, language: lang, origin_url: window.location.origin, priority_code: priority ? priorityCode : undefined, ...guest });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      setBooking({ ...r.data, notes_copy: service.notes_copy });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 409) { setTime(null); setStep(1); }
    } finally {
      setSubmitting(false);
    }
  };

  if (booking) return <BookShell><Confirmation booking={booking} /></BookShell>;

  return (
    <BookShell>
      <p className="text-xs uppercase tracking-[0.25em] text-[#8a7347]">{t.eyebrow}</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light mt-3">{t.title}</h1>
      {catalog?.priority?.active && (
        <p className="mt-6 text-sm bg-[#15262d] text-white px-4 py-3 rounded-sm" data-testid="book-priority-banner">
          {priority ? t.priorityWelcome.replace("{name}", priority.first_name) : t.priorityOnly.replace("{date}", catalog.priority.end)}
        </p>
      )}
      <ol className="flex gap-4 sm:gap-6 mt-8 text-[11px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.18em]" data-testid="book-stepper">
        {t.steps.map((label, i) => (
          <li key={label} className={`whitespace-nowrap ${i === step ? "text-[#15262d] font-semibold" : "text-[#1d2b31]/40"}`}>
            {i + 1}. {label}
          </li>
        ))}
      </ol>
      {service && step > 0 && (
        <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 border-l-2 border-[#b09873] pl-4" data-testid="book-selection">
          <button onClick={() => setStep(step - 1)} data-testid="book-back-step" className="flex items-center gap-1 text-xs text-[#1d2b31]/60 hover:text-[#15262d]">
            <ArrowLeft size={14} /> {t.back}
          </button>
          <span className="font-medium">{service.name}</span>
          <span className="text-sm text-[#1d2b31]/60">{priceLabel(service, t)}</span>
          {step === 2 && <span className="text-sm">{prettyDate(date, locale)} · {time}</span>}
        </div>
      )}
      <div className="mt-8">
        {!catalog && <p className="text-sm text-[#1d2b31]/50">{t.loading}</p>}
        {catalog && step === 0 && <ServicePicker catalog={catalog} activeCat={activeCat} setActiveCat={setActiveCat} onPick={pick} />}
        {step === 1 && service && (
          <>
            <DateTimePicker service={service} date={date} setDate={setDate} time={time} setTime={setTime} onFullDay={setFullDay} />
            {service.notes_copy && <p className="text-xs text-[#8a7347] mt-6 max-w-2xl">{service.notes_copy}</p>}
            {!fullDay && <div className="sticky bottom-0 -mx-6 px-6 py-4 mt-6 bg-[#f6f2ec]/90 backdrop-blur-md sm:static sm:mx-0 sm:px-0 sm:bg-transparent sm:backdrop-blur-none">
              <button disabled={!time} onClick={() => { setStep(2); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="book-continue"
                className="w-full sm:w-auto bg-[#15262d] text-white text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-sm hover:bg-[#b09873] transition-colors disabled:opacity-40 disabled:hover:bg-[#15262d]">
                {time ? `${t.continue} · ${time}` : t.selectTime}
              </button>
            </div>}
          </>
        )}
        {step === 2 && deposit > 0 && (
          <p className="mb-4 text-sm bg-white border-l-4 border-[#b09873] px-4 py-3" data-testid="book-deposit-note">
            {t.depositNote.replace("{n}", deposit)}
          </p>
        )}
        {step === 2 && gated && <PriorityGate end={catalog.priority.end} onAllowed={() => setVerified(true)} />}
        {step === 2 && !gated && <GuestForm onSubmit={submit} submitting={submitting} submitLabel={deposit > 0 ? t.payConfirm : t.confirm} />}
      </div>
    </BookShell>
  );
}
